/**
 * Binding: main-thread task primitives that need JavaScript (`scheduler.yield()`,
 * `scheduler.postTask()`, awaiting a continuation). The policy of *when* to yield lives in Rust
 * (`crates/compute-scheduler/src/chunk.rs`: yield after an 8 ms slice or when input is pending),
 * reached through the `shouldYield` callback the scheduler binding installs.
 *
 * Preference order for a yield: `scheduler.yield()` (Chromium 129+; the continuation keeps its
 * priority and runs before other queued tasks of that priority), then
 * `scheduler.postTask(fn, { priority })` (Chromium 94+), then a `MessageChannel` macrotask (no
 * `setTimeout` 4 ms clamp), then `setTimeout(0)`.
 *
 * @module apps/web/src/lib/compute/mainThread
 */

import type { ComputeLane } from './types';

/** Priority names of the Prioritized Task Scheduling API. */
export type TaskPriority = 'user-blocking' | 'user-visible' | 'background';

/** Lane to `postTask` priority (mirrors `Lane::task_priority` in Rust). */
export const LANE_PRIORITY: Record<ComputeLane, TaskPriority> = {
  interactive: 'user-blocking',
  visible: 'user-visible',
  background: 'background',
};

/** The subset of the global `scheduler` object used here. */
interface PrioritizedScheduler {
  /** Queue a callback at a priority (Chromium). */
  postTask?: (
    callback: () => void,
    options?: {
      /** Task priority. */
      priority?: TaskPriority;
    }
  ) => Promise<unknown>;
  /** Yield with inherited priority (Chromium 129+). */
  yield?: () => Promise<void>;
}

/** The global `scheduler`, when present. */
function prioritizedScheduler(): PrioritizedScheduler | undefined {
  const candidate = (globalThis as { scheduler?: unknown }).scheduler;
  return candidate && typeof candidate === 'object'
    ? (candidate as PrioritizedScheduler)
    : undefined;
}

/** Resolve after a macrotask at `priority`. */
function nextTask(priority: TaskPriority): Promise<void> {
  const native = prioritizedScheduler();
  if (typeof native?.postTask === 'function')
    return native.postTask.call(native, () => undefined, { priority }).then(() => undefined);
  if (typeof MessageChannel !== 'undefined')
    return new Promise(resolve => {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };
      channel.port2.postMessage(null);
    });
  return new Promise(resolve => setTimeout(resolve, 0));
}

/** Yield the main thread so input and rendering run first, then continue. */
export function yieldToMain(priority: TaskPriority = 'user-visible'): Promise<void> {
  const native = prioritizedScheduler();
  if (typeof native?.yield === 'function') return native.yield.call(native);
  return nextTask(priority);
}

/** Yield policy; replaced by the Rust policy once the scheduler core attaches. */
let shouldYield: (sliceElapsedMs: number) => boolean = elapsed => elapsed >= 8;

/** Install the yield policy (called by the scheduler binding with the Rust `shouldYield`). */
export function setYieldPolicy(policy: (sliceElapsedMs: number) => boolean): void {
  shouldYield = policy;
}

/** Options for `runChunked`. */
export interface ChunkOptions {
  /** Priority for fallback yields; defaults to `user-visible`. */
  priority?: TaskPriority;
  /** Stops the loop between items; the promise then rejects with `signal.reason`. */
  signal?: AbortSignal;
}

/**
 * Map `items` through `map` on the main thread in slices, yielding whenever the yield policy
 * says so. Order is preserved; `undefined` results are dropped (filter in the same pass).
 */
export async function runChunked<T, R>(
  items: readonly T[],
  map: (item: T, index: number) => R | undefined,
  options: ChunkOptions = {}
): Promise<R[]> {
  const output: R[] = [];
  let sliceStart = performance.now();
  for (let index = 0; index < items.length; index++) {
    if (options.signal?.aborted) throw options.signal.reason;
    const mapped = map(items[index], index);
    if (mapped !== undefined) output.push(mapped);
    if (index + 1 < items.length && shouldYield(performance.now() - sliceStart)) {
      await yieldToMain(options.priority);
      sliceStart = performance.now();
    }
  }
  if (options.signal?.aborted) throw options.signal.reason;
  return output;
}
